import React, { useEffect, useRef, useState } from "react";
import { useEmailJS } from "../../hooks/useEmailJS";
import { EMAIL_ADDRESS } from "../EmailAddress/EmailAddress";
import { trackGenerateLead } from "../../analytics";
// @ts-ignore
import "./CallbackPopup.css";

const PHONE_TEL = "9294526031";
const PHONE_DISPLAY = "(929) 452-6031";

function normalizePhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

function formatPhone(normalized: string): string {
  const digits = normalized.replace(/\D/g, "").slice(-10);
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

interface CallbackPopupProps {
  onClose: () => void;
}

export default function CallbackPopup({ onClose }: CallbackPopupProps): JSX.Element {
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [requestedPhone, setRequestedPhone] = useState("");
  const { status, send } = useEmailJS();
  const inputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  const leadTracked = useRef(false);
  closeRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeRef.current();
        return;
      }
      if (e.key !== "Tab" || !cardRef.current) return;
      const focusable = Array.from(
        cardRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  useEffect(() => {
    if (status === "success" && !leadTracked.current) {
      leadTracked.current = true;
      trackGenerateLead("callback");
    }
  }, [status]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const normalized = normalizePhone(phone);
    if (!normalized) {
      setError("Please enter a valid 10-digit US phone number.");
      return;
    }
    setError("");
    setRequestedPhone(normalized);
    send({
      from_name: "Website callback request",
      from_email: EMAIL_ADDRESS,
      phone: normalized,
      subject: "Callback request — GS Limo Elite website",
      message: [
        "Callback requested from the website popup.",
        `Phone: ${normalized}`,
        `Page: ${window.location.pathname}`,
        `Requested: ${new Date().toLocaleString()}`,
      ].join("\n"),
      to_email: EMAIL_ADDRESS,
    });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPhone(e.target.value);
    if (error) setError("");
  };

  return (
    <div
      className="gs-cb-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="gs-cb-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gs-cb-title"
        ref={cardRef}
      >
        <button
          type="button"
          className="gs-cb-close"
          onClick={onClose}
          aria-label="Close callback request"
        >
          <i className="fas fa-times" aria-hidden="true"></i>
        </button>

        {status === "success" ? (
          <div className="gs-cb-success">
            <span className="gs-cb-success-icon" aria-hidden="true">
              <i className="fas fa-check-circle"></i>
            </span>
            <h2 className="gs-cb-title" id="gs-cb-title">
              Thanks — we have your number.
            </h2>
            <p className="gs-cb-sub">
              We&rsquo;ll call you back at <strong>{formatPhone(requestedPhone)}</strong> to go over
              your route, availability and pricing.
            </p>
            <div className="gs-cb-success-actions">
              <a
                href={`tel:${PHONE_TEL}`}
                className="btn gs-btn-gold gs-cb-btn"
                title={`Call GS Limo Elite at ${PHONE_DISPLAY}`}
              >
                <i className="fas fa-phone me-2" aria-hidden="true"></i>
                {PHONE_DISPLAY}
              </a>
              <button type="button" className="gs-cb-done" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="gs-cb-eyebrow">Prefer to talk?</p>
            <h2 className="gs-cb-title" id="gs-cb-title">
              Get a <span>callback</span> from our team.
            </h2>
            <p className="gs-cb-sub">
              Drop your number and we&rsquo;ll call you back to review your trip, availability and
              pricing.
            </p>
            <form className="gs-cb-form" onSubmit={handleSubmit} noValidate>
              <label className="visually-hidden" htmlFor="gs-cb-phone">
                Your phone number
              </label>
              <input
                id="gs-cb-phone"
                ref={inputRef}
                className="gs-cb-input"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="Your phone number"
                value={phone}
                onChange={handlePhoneChange}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "gs-cb-error" : undefined}
                disabled={status === "sending"}
              />
              <button
                type="submit"
                className="btn gs-btn-gold gs-cb-btn"
                disabled={status === "sending"}
              >
                {status === "sending" ? (
                  "Sending…"
                ) : (
                  <>
                    Call Me <i className="fas fa-arrow-right" aria-hidden="true"></i>
                  </>
                )}
              </button>
            </form>
            {error && (
              <p className="gs-cb-error" id="gs-cb-error" role="alert">
                {error}
              </p>
            )}
            {status === "error" && (
              <p className="gs-cb-error" role="alert">
                Something went wrong. Please call us instead.
              </p>
            )}
            <p className="gs-cb-note">We only use your number to respond to this request.</p>
            <div className="gs-cb-foot">
              <span>Prefer to call now?</span>
              <a href={`tel:${PHONE_TEL}`} title={`Call GS Limo Elite at ${PHONE_DISPLAY}`}>
                <i className="fas fa-phone" aria-hidden="true"></i> {PHONE_DISPLAY}
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
